/**
 * Client-side GoogleFlightBookingOption model with computed properties and methods
 */

import type { GoogleFlightBookingOption } from '@elara/domain/models/schemas/flight-schemas';
import type { Cost } from '@elara/domain/models/schemas/shared-schemas';

import { GoogleFlightBookingOptionModelSpec } from '@elara/domain/models/TransientModelSpecs';

import { createLocalModel } from '#firebase/createLocalModel.js';
import { formatPrice } from '#lib/utils.js';

export const GoogleFlightBookingOptionModel = createLocalModel(
  GoogleFlightBookingOptionModelSpec,
  (data) => ({
    preferredCost(preferredCurrency?: string): Cost {
      let cost = data.cost;

      if (preferredCurrency) {
        const localCost = data.localCosts?.find((localCost) => localCost.currency.toLowerCase() === preferredCurrency.toLowerCase());
        if (localCost) {
          cost = localCost;
        }
      }

      return cost;
    },

    /**
     * Formatted price string (e.g. "$1,337")
     */
    formattedPrice(paidPassengers: number, preferredCurrency?: string): string {
      const cost = this.preferredCost(preferredCurrency);

      return formatPrice(cost.amount / paidPassengers, cost.currency);
    },

    /**
     * Display title: option title when present, otherwise provider name
     */
    get displayTitle(): string {
      return data.title ?? data.providerName;
    },

    /**
     * Whether this option requires separate ticket bookings (multiple legs)
     */
    get isSeparateTickets(): boolean {
      return (data.legs?.length ?? 0) > 1;
    },

    /**
     * Booking legs (1 = together, 2 = separate tickets)
     */
    get legs() {
      return data.legs ?? [];
    },

    costDescription(totalPaidPassengers: number, preferredCurrency?: string): string {
      const cost = this.preferredCost(preferredCurrency);

      let description = `Total for 1 passenger`;

      if (totalPaidPassengers > 1) {
        description = `${formatPrice(cost.amount, cost.currency)} for all passengers`;
      }

      return description;
    },
  }),
);

export type GoogleFlightBookingOptionModel = ReturnType<
  (typeof GoogleFlightBookingOptionModel)['from']
>;

/**
 * Creates a client-side GoogleFlightBookingOption instance with computed properties
 */
export function createGoogleFlightBookingOptionModel(
  data: GoogleFlightBookingOption,
  index?: number,
): GoogleFlightBookingOptionModel {
  const input = { ...data, id: index !== undefined ? String(index) : undefined };
  return GoogleFlightBookingOptionModel.from(input);
}
